import { useCallback, useEffect, useRef } from 'react';
import { ArrowUp, AudioLines } from 'lucide-react';
import { cn } from '@/shared/cn';
import { KbdKey } from '@/design-system/KbdKey';
import { Commentable } from '@/shared/comments/Commentable';
import { COMPOSER_CARD_SHELL, COMPOSER_CHROME_TRIGGER_SLOT } from '@/shared/ui-class';
import { useChat } from '@/versions/v1/shared/ChatContext';
import { ComposerPlusMenu } from './ComposerPlusMenu';
import { ModelSelector } from './ModelSelector';
import { PaperclipAttachButton } from './PaperclipAttachButton';
import { PermissionSelector } from './PermissionSelector';
import { ThreadNoticeRail } from './ThreadNoticeRail';

const SEND_BUTTON_CLASS =
  'flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-white shadow-sm transition-all duration-200 hover:bg-accent-600 hover:shadow-md hover:-translate-y-px active:translate-y-0 active:[animation:send-pulse_280ms_ease-out] disabled:opacity-40 disabled:shadow-none disabled:hover:-translate-y-0 dark:bg-accent-500 dark:hover:bg-accent-400';

const MIC_BUTTON_CLASS =
  'flex h-8 w-8 items-center justify-center rounded-md text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-100 dark:focus-visible:ring-zinc-700';

export function ChatComposer({ welcome = false }: { welcome?: boolean }) {
  const {
    draft,
    setDraft,
    send,
    editingMessageId,
    editDraft,
    setEditDraft,
    setEditingMessageId,
    messages,
  } = useChat();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isEditing = editingMessageId !== null;
  const placeholder = welcome
    ? 'How can I help you today?'
    : messages.length === 0
      ? 'How can I help you today?'
      : 'Message the assistant…';

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 6 * 24;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [draft, editDraft, isEditing, resizeTextarea]);

  const value = isEditing ? editDraft : draft;
  const setValue = isEditing ? setEditDraft : setDraft;

  const submit = () => {
    if (isEditing) {
      send(editDraft);
      setEditingMessageId(null);
      setEditDraft('');
      return;
    }
    send(draft);
  };

  const card = (
    <div className={cn(COMPOSER_CARD_SHELL, '@container')}>
      <textarea
        ref={textareaRef}
        rows={3}
        value={value}
        placeholder={placeholder}
        aria-label="Message the assistant"
        data-composer="thread"
        className="block min-h-[5.5rem] w-full resize-none bg-transparent px-4 pb-3 pt-4 text-base leading-relaxed outline-none placeholder:font-light placeholder:text-zinc-500/80 dark:placeholder:text-zinc-400/70"
        onChange={(e) => {
          setValue(e.target.value);
          resizeTextarea();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape' && isEditing) {
            e.preventDefault();
            setEditingMessageId(null);
            setEditDraft('');
          }
        }}
      />
      <div className="flex min-w-0 items-center gap-1 px-2 pb-2">
        <div className="shrink-0">
          <ComposerPlusMenu />
        </div>
        <div className="shrink-0">
          <PaperclipAttachButton />
        </div>
        <div className={COMPOSER_CHROME_TRIGGER_SLOT}>
          <ModelSelector />
        </div>
        <div className={COMPOSER_CHROME_TRIGGER_SLOT}>
          <PermissionSelector />
        </div>
        <div className="ml-auto flex min-w-0 shrink items-center gap-1">
          <button
            type="button"
            aria-label="Start dictation"
            title="Start dictation"
            className={MIC_BUTTON_CLASS}
          >
            <AudioLines className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!value.trim()}
            aria-label="Send message"
            title="Send message"
            className={SEND_BUTTON_CLASS}
          >
            <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );

  if (welcome) {
    return <Commentable name="Composer">{card}</Commentable>;
  }

  return (
    <Commentable name="Composer">
      <div className="sticky bottom-0 z-10 mx-auto w-full max-w-[var(--thread-max-width)] bg-[var(--background)] px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 sm:pb-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-12 h-12 bg-gradient-to-t from-[var(--background)] to-transparent"
        />
        <ThreadNoticeRail />
        {card}
        <div className="mt-2 flex items-center justify-between text-xs text-zinc-400 dark:text-zinc-500">
          <span className="hidden items-center gap-1 sm:inline-flex">
            <KbdKey>↵</KbdKey>
            to send
            <span aria-hidden className="px-1 text-zinc-300 dark:text-zinc-600">
              ·
            </span>
            <KbdKey>⇧</KbdKey>
            <KbdKey>↵</KbdKey>
            for new line
          </span>
          <span>© Airis Analytica</span>
        </div>
      </div>
    </Commentable>
  );
}
